import { GameScene } from '@Core/GameScene/GameScene';
import { TPoint } from '@GFTypes/Point';
import { GameStore } from '@GameStore/GameStore';

export default class ReelSpinningSimulationBody {
    private readonly gameStore: GameStore;

    public scene: GameScene;
    public startingPosition: TPoint;
    public id: number;
    public positionY: number;
    public velocity: number;
    public acceleration: number;
    public drag: number;
    public maxVelocity: number;

    private stoppingMotion:
        | {
              velocity: number;
              distance: number;
              brakingDistance: number;
              coastTime: number;
              brakingTime: number;
              elapsed: number;
              travelled: number;
              finished: boolean;
          }
        | undefined;

    constructor(
        scene: GameScene,
        position: TPoint,
        id: number,
        options: Partial<{
            velocity: number;
            acceleration: number;
            drag: number;
            maxVelocity: number;
        }> = {}
    ) {
        this.scene = scene;
        this.startingPosition = position;
        this.gameStore = scene.game.gameStore;
        this.id = id;
        this.positionY = this.startingPosition.y ?? 0;
        this.velocity = options.velocity ?? 0;
        this.acceleration = options.acceleration ?? 0;
        this.drag = options.drag ?? 1;
        this.maxVelocity = options.maxVelocity ?? 0;
    }

    setPositionY(value: number): this {
        this.positionY = value;
        return this;
    }

    setVelocity(value: number): this {
        this.stoppingMotion = undefined;
        this.velocity = value;
        return this;
    }

    setAcceleration(value: number): this {
        this.stoppingMotion = undefined;
        this.acceleration = value;
        return this;
    }

    setDrag(value: number): this {
        this.drag = value;
        return this;
    }

    setMaxVelocity(value: number): this {
        this.maxVelocity = value;
        return this;
    }

    stopAfterDistance(distance: number, brakingDistance: number, velocity: number = this.velocity): this {
        if (!Number.isFinite(distance) || distance < 0 || !Number.isFinite(brakingDistance) || brakingDistance < 0) {
            throw new Error('Stopping distances must be finite and non-negative.');
        }
        if (!Number.isFinite(velocity) || (distance > 0 && velocity <= 0)) {
            throw new Error('Stopping over a positive distance requires a positive velocity.');
        }

        const cappedDistance = Math.min(brakingDistance, distance);
        this.acceleration = 0;
        this.velocity = distance === 0 ? 0 : velocity;
        this.stoppingMotion = {
            velocity: this.velocity,
            distance,
            brakingDistance: cappedDistance,
            coastTime: distance === 0 ? 0 : (distance - cappedDistance) / velocity,
            brakingTime: distance === 0 ? 0 : (2 * cappedDistance) / velocity,
            elapsed: 0,
            travelled: 0,
            finished: distance === 0
        };
        return this;
    }

    public hasFinishedStopping(): boolean {
        return this.stoppingMotion?.finished ?? false;
    }

    private updateStoppingMotion(dt: number): number {
        const motion = this.stoppingMotion!;
        if (motion.finished) return this.positionY;

        const duration = motion.coastTime + motion.brakingTime;
        motion.elapsed = Math.min(motion.elapsed + dt, duration);
        let travelled: number;

        if (motion.elapsed >= duration) {
            travelled = motion.distance;
            this.velocity = 0;
            motion.finished = true;
        } else if (motion.elapsed <= motion.coastTime) {
            travelled = motion.velocity * motion.elapsed;
            this.velocity = motion.velocity;
        } else {
            const progress = (motion.elapsed - motion.coastTime) / motion.brakingTime;
            travelled = motion.distance - motion.brakingDistance + motion.brakingDistance * progress * (2 - progress);
            this.velocity = motion.velocity * (1 - progress);
        }

        this.positionY += travelled - motion.travelled;
        motion.travelled = travelled;
        return this.positionY;
    }

    update(dt: number): number {
        if (!Number.isFinite(dt) || dt <= 0) return this.positionY;
        if (this.stoppingMotion) return this.updateStoppingMotion(dt);

        if (this.acceleration !== 0) {
            const newVelocity = this.velocity + this.acceleration * dt;
            if ((this.velocity > 0 && newVelocity < 0) || (this.velocity < 0 && newVelocity > 0)) {
                this.velocity = 0;
            } else {
                this.velocity = newVelocity;
            }
        } else if (this.drag !== 0) {
            const dragAmount = this.drag * dt;

            if (this.velocity - dragAmount > 0) {
                this.velocity -= dragAmount;
            } else if (this.velocity + dragAmount < 0) {
                this.velocity += dragAmount;
            } else {
                this.velocity = 0;
            }
        }

        if (this.maxVelocity !== 0) {
            if (this.velocity > this.maxVelocity) this.velocity = this.maxVelocity;
            else if (this.velocity < -this.maxVelocity) this.velocity = -this.maxVelocity;
        }

        this.positionY += this.velocity * dt;

        return this.positionY;
    }
}
